import React, { useState, useEffect } from 'react';
import { AlertCircle, Clock, X, ArrowRight, CreditCard } from 'lucide-react';
import dayjs from 'dayjs';

const PendingPaymentModal = ({ isOpen, onClose, accessRevokeDate, notificationReleasedDate, continueText = "Continue" }) => {
  const revokeDate = dayjs(notificationReleasedDate).add(48, "hour");
  const [timeLeft, setTimeLeft] = useState({
    hours: "00",
    minutes: "00",
    seconds: "00"
  });

  useEffect(() => {
    if (!isOpen) return;

    const calculateTimeLeft = () => {
      const now = dayjs();
      const diffInSeconds = revokeDate.diff(now, "second");

      if (diffInSeconds > 0) {
        const h = Math.floor(diffInSeconds / 3600);
        const m = Math.floor((diffInSeconds % 3600) / 60);
        const s = diffInSeconds % 60;

        setTimeLeft({
          hours: h.toString().padStart(2, "0"),
          minutes: m.toString().padStart(2, "0"),
          seconds: s.toString().padStart(2, "0")
        });
      } else {
        setTimeLeft({ hours: "00", minutes: "00", seconds: "00" });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [accessRevokeDate, isOpen]);

  if (!isOpen) return null;

  const daysLeft = revokeDate.diff(dayjs(), "day");
  const formattedDeadline = revokeDate.format("DD MMMM YYYY");

  const isExpired = daysLeft < 0;
  const isUrgent = daysLeft <= 3;

  const theme = isExpired
    ? { color: '#dc2626', bg: '#fef2f2', icon: AlertCircle, title: 'Access Restriction Alert' }
    : isUrgent
      ? { color: '#e11d48', bg: '#fff1f2', icon: Clock, title: 'Action Required: Payment Pending' }
      : { color: '#2563eb', bg: '#eff6ff', icon: Clock, title: 'Remaining Payment Reminder' };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in slide-in-from-bottom-4 duration-400">
        {/* Header with Icon */}
        <div className="p-6 pb-0 flex justify-between items-start">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center`} style={{ backgroundColor: theme.bg }}>
            <theme.icon size={24} color={theme.color} strokeWidth={2.5} />
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {theme.title}
          </h2>

          <div className="space-y-4">
            <p className="text-gray-600 leading-relaxed">
              {isExpired ? (
                <>Your dashboard access is partially revoked. Please pay the remaining amount to get the access back.</>
              ) : (
                <div className="flex flex-col gap-3">
                  <span>Kindly clear your pending program fee within the next 48 hours to avoid restriction in access to your student dashboard and learning portal.</span>

                  <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Time Remaining:</span>
                    <div className="flex gap-2 items-center">
                      <div className="flex flex-col items-center">
                        <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-200 min-w-[45px] flex items-center justify-center">
                          <span className="text-xl font-bold font-mono" style={{ color: theme.color }}>{timeLeft.hours}</span>
                        </div>
                        <span className="text-[10px] font-bold text-gray-400 mt-1">HOURS</span>
                      </div>
                      <span className="text-xl font-bold mb-5" style={{ color: theme.color }}>:</span>
                      <div className="flex flex-col items-center">
                        <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-200 min-w-[45px] flex items-center justify-center">
                          <span className="text-xl font-bold font-mono" style={{ color: theme.color }}>{timeLeft.minutes}</span>
                        </div>
                        <span className="text-[10px] font-bold text-gray-400 mt-1">MINS</span>
                      </div>
                      <span className="text-xl font-bold mb-5" style={{ color: theme.color }}>:</span>
                      <div className="flex flex-col items-center">
                        <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-200 min-w-[45px] flex items-center justify-center">
                          <span className="text-xl font-bold font-mono" style={{ color: theme.color }}>{timeLeft.seconds}</span>
                        </div>
                        <span className="text-[10px] font-bold text-gray-400 mt-1">SECS</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </p>

            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-sm text-gray-500">
                If you have any doubts, please reach out to{' '}
                <a
                  href="mailto:academic@accredian.com"
                  className="text-blue-600 font-semibold hover:underline"
                >
                  academics@accredian.com
                </a>
              </p>
            </div>

            {/* Status Badge */}

          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-gray-50 flex flex-col gap-3">
          <a
            href="/Duefee"
            className="w-full py-3 px-4 rounded-xl flex items-center justify-center gap-2 font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/25"
            style={{ backgroundColor: theme.color }}
          >
            <CreditCard size={18} />
            Pay Now
            <ArrowRight size={18} />
          </a>
          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-xl font-semibold text-gray-600 hover:bg-gray-200 transition-colors"
          >
            {continueText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-in.fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-in.slide-in-from-bottom-4 {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PendingPaymentModal;
